import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { trackPageview, trackLeave } from "@/lib/analytics";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ClientDashboard from "@/pages/ClientDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import VerifyEmail from "@/pages/VerifyEmail";
import Support from "@/pages/Support";

function homeFor(role) {
  return role === "admin" ? "/admin" : role === "employee" ? "/employee" : "/dashboard";
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.email_verified === false) return <Navigate to="/verify" replace />;
  if (role && user.role !== role) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function VerifyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.email_verified !== false) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function AnalyticsTracker() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const prev = useRef({ path: null, ts: 0 });

  const isStaff = user && (user.role === "admin" || user.role === "employee");
  const role = user ? user.role : "visitor";

  useEffect(() => {
    if (loading) return;        // wait until we know who the user is
    if (isStaff) return;        // never count admins or employees
    const path = location.pathname;
    const now = Date.now();
    if (prev.current.path && prev.current.path !== path) {
      trackLeave(prev.current.path, now - prev.current.ts, role);
    }
    const ref = prev.current.path ? window.location.origin + prev.current.path : document.referrer;
    trackPageview(path, ref, role);
    prev.current = { path, ts: now };
    // eslint-disable-next-line
  }, [location.pathname, loading, isStaff]);

  useEffect(() => {
    const onHide = () => {
      if (isStaff) return;
      if (document.visibilityState === "hidden" && prev.current.path) {
        trackLeave(prev.current.path, Date.now() - prev.current.ts, role);
      }
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
    // eslint-disable-next-line
  }, [isStaff, role]);

  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/support" element={<Support />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<Protected role="client"><ClientDashboard /></Protected>} />
            <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
            <Route path="/employee" element={<Protected role="employee"><EmployeeDashboard /></Protected>} />
            <Route path="/verify" element={<VerifyRoute><VerifyEmail /></VerifyRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
